import { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import * as d3 from 'd3';
import { LineChartProps, TooltipData, ChartMode } from '@/types';
import { useChartDimensions } from '@/hooks/useChartDimensions';
import { useD3Zoom } from '@/hooks/useD3Zoom';
import { 
  createScales, 
  createLineGenerator, 
  findNearestPoints, 
  formatNumber, 
  formatDate,
  formatShortDate,
  debounce,
  validateSeriesData 
} from '@/utils/chartUtils';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { AxisBottom, AxisLeft } from '@/components/charts/shared/Axis';

// Interactive multi-line chart with zoom and tooltips
export default function LineMultiChart({ 
  series, 
  height, 
  mode = 'absolute', 
  dimensions: customDimensions 
}: LineChartProps) {
  const ref = useRef<SVGSVGElement | null>(null);
  const [hover, setHover] = useState<TooltipData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTransform, setCurrentTransform] = useState<d3.ZoomTransform | null>(null);

  const dimensions = useChartDimensions({ 
    height, 
    ...customDimensions 
  });

  const isValidData = useMemo(() => validateSeriesData(series), [series]);
  
  // Create scales and line generator - use empty series as fallback to avoid errors
  const { x, y, color, normalized } = useMemo(() => {
    if (!isValidData) {
      // Return minimal valid scales for empty data
      const emptyX = d3.scaleTime().domain([new Date(), new Date()]).range([dimensions.margin.left, dimensions.width - dimensions.margin.right]);
      const emptyY = d3.scaleLinear().domain([0, 1]).range([dimensions.height - dimensions.margin.bottom, dimensions.margin.top]);
      const emptyColor = d3.scaleOrdinal<string, string>(d3.schemeTableau10).domain([]);
      return { x: emptyX, y: emptyY, color: emptyColor, normalized: [] };
    }
    return createScales(series, dimensions, mode);
  }, [series, dimensions, mode, isValidData]);

  const line = useMemo(() => 
    createLineGenerator(x, y), 
    [x, y]
  );

  const handleZoom = useCallback((event: d3.D3ZoomEvent<SVGSVGElement, unknown>) => {
    const svg = d3.select(ref.current);
    if (!svg.node()) return;
    
    svg.select('g.chart-content').attr('transform', event.transform.toString());
    
    const gX = svg.select<SVGGElement>('g.x-axis');
    const zx = event.transform.rescaleX(x);
    gX.call(d3.axisBottom(zx).ticks(6) as any);
    
    setCurrentTransform(event.transform);
  }, [x]);

  const debouncedMouseHandler = useMemo(() => {
    return debounce((svg: SVGSVGElement, clientX: number, clientY: number) => {
      const rect = svg.getBoundingClientRect();
      const scaleX = svg.clientWidth / dimensions.width;
      const mouseX = (clientX - rect.left) / scaleX;
      
      // Adjust mouse position to account for chart content positioning
      const adjustedMouseX = mouseX - dimensions.margin.left;
      const currentXScale = currentTransform ? currentTransform.rescaleX(x) : x;
      const xm = currentXScale.invert(adjustedMouseX);
      const nearest = findNearestPoints(normalized, xm);
      
      setHover({ x: adjustedMouseX, date: xm, nearest });
    }, 16);
  }, [x, normalized, currentTransform, dimensions.width, dimensions.margin.left]);

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGRectElement>) => {
    const svg = e.currentTarget.ownerSVGElement;
    if (svg) {
      debouncedMouseHandler(svg, e.clientX, e.clientY);
    }
  }, [debouncedMouseHandler]);

  const handleMouseLeave = useCallback(() => {
    setHover(null);
  }, []);

  useD3Zoom({
    svgRef: ref,
    dimensions,
    onZoom: handleZoom
  });

  // Early returns after all hooks
  if (!isValidData) {
    return (
      <ErrorBoundary>
        <div className="flex items-center justify-center p-8 bg-neutral-900/50 border border-neutral-800 rounded-2xl">
          <p className="text-neutral-400">Invalid or empty data provided</p>
        </div>
      </ErrorBoundary>
    );
  }

  if (isLoading) {
    return <LoadingSpinner message="Loading chart..." />;
  }

  return (
    <ErrorBoundary>
      <div className="w-full overflow-hidden rounded-2xl bg-neutral-950 border border-neutral-800">
        <svg 
          ref={ref} 
          viewBox={`0 0 ${dimensions.width} ${dimensions.height}`} 
          role="img" 
          aria-label="Interactive line chart showing data trends over time"
        >
          <g className="x-axis" transform={`translate(${dimensions.margin.left},${dimensions.height - dimensions.margin.bottom})`}>
            <AxisBottom scale={x} ticks={6} format={formatShortDate} />
          </g>
          <g transform={`translate(${dimensions.margin.left},${dimensions.margin.top})`}>
            <AxisLeft scale={y} ticks={5} format={(d: number) => formatNumber(d, mode)} />
          </g>

          <g className="chart-content" transform={`translate(${dimensions.margin.left},${dimensions.margin.top})`}>
            {normalized.map(s => (
              <path 
                key={s.id} 
                className="series" 
                d={line(s.values) ?? undefined} 
                fill="none" 
                stroke={color(s.id)} 
                strokeWidth={2}
                aria-label={`${s.id} data series`}
              />
            ))}
          </g>

          <g transform={`translate(${dimensions.width - 150}, ${dimensions.margin.top})`} role="list" aria-label="Legend">
            {normalized.map((s, i) => (
              <g key={s.id} transform={`translate(0, ${i * 18})`} role="listitem">
                <rect width={10} height={10} fill={color(s.id)} rx={2} aria-hidden="true" />
                <text x={16} y={10} className="fill-neutral-300 text-[12px]" aria-label={`${s.id} series`}>
                  {s.id}
                </text>
              </g>
            ))}
          </g>

          <rect 
            x={dimensions.margin.left} 
            y={dimensions.margin.top} 
            width={dimensions.width - dimensions.margin.left - dimensions.margin.right} 
            height={dimensions.height - dimensions.margin.top - dimensions.margin.bottom}
            fill="transparent" 
            onMouseMove={handleMouseMove} 
            onMouseLeave={handleMouseLeave}
            aria-label="Interactive chart area"
          />

          {hover && (
            <g transform={`translate(${hover.x + dimensions.margin.left}, ${dimensions.margin.top})`} pointerEvents="none">
              <line 
                x1={0} x2={0} 
                y1={0} y2={dimensions.height - dimensions.margin.top - dimensions.margin.bottom} 
                stroke="white" 
                opacity={0.2}
                aria-hidden="true"
              />
              <foreignObject x={8} y={8} width={220} height={140}>
                <div className="bg-neutral-800/90 border border-neutral-700 rounded-lg p-2 text-xs text-neutral-100">
                  <div className="opacity-80 mb-1">{formatDate(hover.date)}</div>
                  {hover.nearest.map((n: any) => (
                    <div key={n.id} className="flex justify-between gap-2">
                      <span className="opacity-80">{n.id}</span>
                      <span>{formatNumber(n.y, mode)}</span>
                    </div>
                  ))}
                </div>
              </foreignObject>
            </g>
          )}
        </svg>
      </div>
    </ErrorBoundary>
  );
}
